import React from "react";

import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { DefaultRenderCell } from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";

import { FillBar, STATUS_COLORS, formatLakhs, formatNumber, growthPct, round1dp } from "./reportUi";

const NUM = { headerAlign: "center", align: "center" };
const TXT = { headerAlign: "left", align: "left" };

/** Wraps a plain value formatter so it renders through TanstackDatagrid's default cell chrome
 * (keeps column alignment) instead of MUI's `valueFormatter`, which this grid doesn't have. */
const withFormatter = (formatter) => ({ value, column }) => (
	<DefaultRenderCell value={formatter(value)} column={column} />
);

const StatusChip = ({ value }) => (
	<Chip
		label={value || "PENDING"}
		size="small"
		sx={{
			height: 22,
			fontSize: 10.5,
			fontWeight: 700,
			letterSpacing: "0.04em",
			color: "#fff",
			bgcolor: STATUS_COLORS[value] || grey[400],
		}}
	/>
);

const Muted = ({ value }) => (
	<Typography fontSize={12.5} color={value ? grey[800] : grey[400]} noWrap>
		{value || "—"}
	</Typography>
);

export const fillingColumns = [
	{ key: "store_name", name: "Store", flex: 1.3, minWidth: 150, ...TXT },
	{ key: "mapping_code", name: "Code", flex: 0.7, minWidth: 90, ...TXT },
	{ key: "store_grade", name: "Grade", flex: 0.5, minWidth: 70, headerAlign: "center", align: "center" },
	{
		key: "approval_status",
		name: "Approval Stage",
		flex: 1,
		minWidth: 130,
		headerAlign: "center",
		align: "center",
		renderCell: ({ value }) => <StatusChip value={value} />,
	},
	{
		key: "fill_pct",
		name: "MRP Fill",
		flex: 1,
		minWidth: 120,
		headerAlign: "center",
		align: "center",
		renderCell: ({ value }) => <FillBar value={value} />,
	},
	{
		key: "current_count",
		name: "Rows Done",
		flex: 0.7,
		minWidth: 95,
		...NUM,
		renderCell: ({ row }) => (
			<Typography fontSize={12.5} color={grey[800]}>
				{formatNumber(row.current_count)} / {formatNumber(row.total_count)}
			</Typography>
		),
	},
	{ key: "submission_count", name: "Subs", flex: 0.45, minWidth: 65, ...NUM },
	{
		key: "approval_roles",
		name: "Approvals",
		flex: 1.1,
		minWidth: 130,
		...TXT,
		renderCell: ({ value }) => <Muted value={value} />,
	},
	{
		key: "created_by_name",
		name: "Owner",
		flex: 1,
		minWidth: 120,
		...TXT,
		renderCell: ({ value }) => <Muted value={value} />,
	},
];

/** Primary value with a small signed growth-vs-LTL badge underneath — keeps the growth
 * context visible without a dedicated column (and out of the raw Excel export). */
const ValueWithGrowth = ({ value, growth, suffix = "%" }) => {
	const g = growth == null || Number.isNaN(Number(growth)) ? null : round1dp(growth);
	return (
		<Stack alignItems="center" spacing={0} sx={{ lineHeight: 1.15 }}>
			<Typography fontSize={12.5} color={grey[800]}>
				{value}
			</Typography>
			{g != null && (
				<Typography fontSize={10} fontWeight={700} color={g >= 0 ? "success.main" : "error.main"}>
					{g > 0 ? "+" : ""}
					{g}
					{suffix}
				</Typography>
			)}
		</Stack>
	);
};

export const manualColumns = [
	{ key: "store_name", name: "Store", flex: 3, minWidth: 150, ...TXT },
	{ key: "mapping_code", name: "Code", flex: 1, minWidth: 90, ...TXT },
	{
		key: "ltl_revenue",
		name: "LTL Rev",
		flex: 1,
		minWidth: 95,
		...NUM,
		renderCell: withFormatter(formatLakhs),
	},
	{ key: "ltl_qty", name: "LTL Qty", flex: 1, minWidth: 85, ...NUM },
	{ key: "ltl_asp", name: "LTL ASP", flex: 1, minWidth: 85, ...NUM },
	{
		key: "ltl_dis",
		name: "LTL Dis",
		flex: 1,
		minWidth: 80,
		...NUM,
		renderCell: withFormatter((value) => `${round1dp(value)}%`),
	},
	{
		key: "new_revenue",
		name: "New Rev",
		flex: 1,
		minWidth: 100,
		...NUM,
		renderCell: ({ value, row }) => (
			<ValueWithGrowth value={formatLakhs(value)} growth={row.new_vs_ltl_pct} />
		),
	},
	{
		key: "new_asp",
		name: "New ASP",
		flex: 1,
		minWidth: 95,
		...NUM,
		renderCell: ({ value, row }) => <ValueWithGrowth value={value} growth={row.new_asp_vs_ltl_pct} />,
	},
	{
		key: "new_dis",
		name: "New Dis",
		flex: 1,
		minWidth: 95,
		...NUM,
		renderCell: ({ value, row }) => (
			<ValueWithGrowth
				value={`${round1dp(value)}%`}
				growth={row.new_dis_vs_ltl_pts}
				suffix=" pts"
			/>
		),
	},
];

/** Group labels come from the API so the first two columns adapt to group_by. */
export const buildMrpColumns = (labels = ["Category", ""]) => {
	const [label1, label2] = labels;
	const cols = [{ key: "group_1", name: label1 || "Group", flex: 1.2, minWidth: 140, ...TXT }];

	if (label2) {
		cols.push({ key: "group_2", name: label2, flex: 1, minWidth: 120, ...TXT });
	}

	return [
		...cols,
		{ key: "ltl_qty", name: "PRV QTY", flex: 0.7, minWidth: 85, ...NUM },
		{
			key: "new_qty",
			name: "NEW QTY",
			flex: 0.8,
			minWidth: 95,
			...NUM,
			renderCell: ({ value, row }) => (
				<ValueWithGrowth value={formatNumber(value)} growth={growthPct(value, row.ltl_qty)} />
			),
		},
		{
			key: "ltl_dis",
			name: "PRV DIS",
			flex: 0.65,
			minWidth: 80,
			...NUM,
			renderCell: withFormatter((value) => `${round1dp(value)}%`),
		},
		{
			key: "new_dis",
			name: "NEW DIS",
			flex: 0.8,
			minWidth: 95,
			...NUM,
			renderCell: ({ value, row }) => (
				<ValueWithGrowth
					value={`${round1dp(value)}%`}
					growth={round1dp((Number(value) || 0) - (Number(row.ltl_dis) || 0))}
					suffix=" pts"
				/>
			),
		},
		{ key: "ltl_opt", name: "PRV OPT", flex: 0.6, minWidth: 78, ...NUM },
		{
			key: "new_opt",
			name: "NEW OPT",
			flex: 0.7,
			minWidth: 90,
			...NUM,
			renderCell: ({ value, row }) => (
				<ValueWithGrowth value={formatNumber(value)} growth={growthPct(value, row.ltl_opt)} />
			),
		},
		{
			key: "ltl_st",
			name: "PRV ST",
			flex: 0.6,
			minWidth: 76,
			...NUM,
			renderCell: withFormatter((value) => `${round1dp(value)}%`),
		},
		{ key: "ltl_asp", name: "PRV ASP", flex: 0.65, minWidth: 82, ...NUM },
		{
			key: "new_asp",
			name: "NEW ASP",
			flex: 0.75,
			minWidth: 95,
			...NUM,
			renderCell: ({ value, row }) => (
				<ValueWithGrowth value={formatNumber(value)} growth={growthPct(value, row.ltl_asp)} />
			),
		},
		{
			key: "ltl_nsv",
			name: "PRV NSV",
			flex: 0.75,
			minWidth: 90,
			...NUM,
			renderCell: withFormatter(formatLakhs),
		},
		{
			key: "new_nsv",
			name: "NEW NSV",
			flex: 0.85,
			minWidth: 105,
			...NUM,
			renderCell: ({ value, row }) => (
				<ValueWithGrowth value={formatLakhs(value)} growth={growthPct(value, row.ltl_nsv)} />
			),
		},
	];
};

export const MRP_GROUP_OPTIONS = [
	{ key: "category", label: "Category" },
	{ key: "sub_category", label: "Category → Sub Category" },
	{ key: "sub_brand", label: "Sub Brand" },
	{ key: "mrp", label: "Category → MRP" },
	{ key: "store", label: "Store" },
	{ key: "store_category", label: "Store → Category" },
];
